import React from "react";
import { Helmet } from "react-helmet";
import { ShareSection } from "../../components/template";

// Hashtag section
const HashtagSection = ({ tags }) => (
  <div className="mt-6 flex flex-wrap md:gap-3 gap-1 justify-center md:mb-6 mb-3">
    {tags.map((tag, index) => (
      <span
        key={index}
        className="bg-red-700 bg-opacity-10 text-red-700 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 m-1 mb-2 hover:bg-red-700 hover:text-white"
      >
        {tag}
      </span>
    ))}
  </div>
);

const Blog11 = () => {
  const hashtags = [
    "#mentalhealth",
    "#selfcare",
    "#dailyhabits",
    "#innerpeace",
    "#ParamLife",
  ];

  return (
    <>
      <Helmet>
        <title>11 Simple Habits That Protect Your Mental Health Daily</title>
        <meta
          name="description"
          content="11 simple, human habits that protect your mental health daily and help you choose peace, one small step at a time."
        />
      </Helmet>

      <article className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed text-gray-800">
        <h1 className="text-secondary text-left text-3xl font-semibold mb-8">
          11 Simple Habits That Protect Your Mental Health Daily
        </h1>
   <img
          src="/assets/knowyourself/blogs/blog11.png"
          alt="7 Things Our Grandparents Did Right (But We Ruined)"
          className="w-[80%] max-w-xl rounded-2xl shadow-md mx-auto mb-10"
        />

        <section className="space-y-4 mb-8">
          <p>
            I’ll be honest with you — I didn’t always take my mental health
            seriously. For years, I thought “I’m fine” meant I was actually fine.
            I thought exhaustion was normal. I thought being constantly busy meant
            I was doing something right.
          </p>

          <p className="text-[#578d48] font-semibold">
            But here’s what I learned the hard way: you can’t build peace on top
            of burnout.
            <br />
            You can’t love others when you’re running on empty.
          </p>

          <p>
            If you’re reading this, maybe you’ve been there too — running fast,
            carrying too much, pretending you’re okay when deep down you’re not.
            I get it. Life gets heavy. And peace can feel like a luxury that’s out
            of reach.
          </p>

          <p className="text-[#578d48] font-semibold">
            But peace isn’t a luxury. It’s survival.
          </p>

          <p>
            And protecting your mental health isn’t about some perfect routine —
            it’s about small, human habits that make you feel like yourself again.
          </p>

          <p>
            Let me share 11 of those with you.
            <br />
            Not from theory. From experience.
          </p>
        </section>

        <section className="space-y-6 mb-8">
          <div>
            <h3 className="text-primary font-semibold">
              1. Start Your Morning Before the World Does
            </h3>
            <p>
              Before the world demands something from you, give something to
              yourself.
            </p>
            <p>
              Don’t reach for your phone. Don’t dive into chaos. Just sit.
              <br />
              Open the window. Breathe. Let your thoughts catch up with your body.
            </p>
            <p>
              You’d be amazed how much peace comes from 10 quiet minutes in the
              morning.
              <br />
              No one’s asking for your attention. No pressure. Just you and the
              day.
            </p>
            <p className="text-[#578d48]">
              That’s how you take your power back.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              2. Step Into the Sunlight — Literally
            </h3>
            <p>I used to underestimate how powerful sunlight is.</p>
            <p>
              Then I realized: every time I stood outside, felt the morning air,
              and let the sun hit my skin — something in me shifted.
            </p>
            <p className="text-[#578d48]">
              Sunlight isn’t just warmth. It’s a reminder that light always
              returns, even after long nights.
            </p>
            <p>
              So step outside. Let your body remember what “alive” feels like.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              3. Drink Water Like You Mean It
            </h3>
            <p>
              You’re probably rolling your eyes — “Drink water? Really?”
              <br />
              Yeah, really.
            </p>
            <p className="text-[#578d48]">
              Because here’s the thing: dehydration makes anxiety worse.
            </p>
            <p>
              And half the time, our “mental fog” is just our body begging us to
              slow down and hydrate.
            </p>
            <p>
              So here’s my rule — before coffee, before chaos, I drink a full
              glass of water.
              <br />
              It’s simple. But it tells my body, I’m listening to you.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              4. Clean One Corner — Just One
            </h3>
            <p>
              There’s something almost spiritual about cleaning a small space.
            </p>
            <p>
              When I feel overwhelmed, I don’t try to fix my whole life. I just
              tidy up one area — maybe my desk, maybe my bed.
            </p>
            <p className="text-[#578d48]">
              It’s a way of saying, “I can’t control everything, but I can create
              calm right here.”
            </p>
            <p>
              And sometimes that’s enough to shift the whole day.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              5. Stop Being Your Own Critic
            </h3>
            <p>
              You wouldn’t talk to your best friend the way you talk to yourself
              on bad days.
            </p>
            <p>
              You know it’s true. I used to beat myself up for every small mistake
              — missed calls, tired mornings, unproductive days.
            </p>
            <p className="text-[#578d48] font-semibold">
              Until I realized: Growth starts when you stop attacking yourself.
            </p>
            <p>
              Now, when I catch myself spiraling, I pause and say: Hey, I’m doing
              my best.
            </p>
            <p>And most days, that’s enough.</p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              6. Move Because You Deserve To — Not Because You Have To
            </h3>
            <p>
              I used to “work out” because I hated my body.
              <br />
              Now I move because I finally love it enough to take care of it.
            </p>
            <p>
              You don’t need the gym. You don’t need perfection.
              <br />
              Just move — walk, stretch, dance in your living room.
            </p>
            <p className="text-[#578d48]">
              Movement isn’t about changing your body. It’s about freeing your
              energy.
            </p>
            <p>
              Every time you move, you release a little bit of the weight you’ve
              been carrying inside.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              7. Write What You Can’t Say Out Loud
            </h3>
            <p>
              Your mind can only hold so much before it starts to ache.
              <br />
              That’s where writing helps.
            </p>
            <p>
              I write when I can’t explain what I’m feeling.
              <br />
              I write without filters — no grammar, no goals. Just honesty.
            </p>
            <p className="text-[#578d48]">
              And somewhere between the first sentence and the last, I always
              feel a little lighter.
            </p>
            <p>
              Because writing isn’t about solving — it’s about releasing.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              8. Protect Your Peace Like It’s Sacred (Because It Is)
            </h3>
            <p>The internet is loud. People are louder.</p>
            <p>
              Everyone has an opinion. Everyone has energy.
              <br />
              And not all energy deserves access to you.
            </p>
            <p>
              Mute, unfollow, step away.
              <br />
              You don’t owe anyone constant access to your mind.
            </p>
            <p className="text-[#578d48] font-semibold">
              Protecting your peace isn’t selfish — it’s self-respect.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              9. Learn to Say “No” Without Explaining
            </h3>
            <p className="text-[#578d48]">
              Here’s something that changed my life:
              <br />
              You don’t need a reason to rest.
            </p>
            <p>
              You don’t need to justify your boundaries to people who keep
              crossing them.
            </p>
            <p>
              A simple “No, not today” is a full sentence.
            </p>
            <p>
              It’s hard at first — especially if you’ve spent your life pleasing
              others — but it’s freeing.
            </p>
            <p className="text-[#578d48]">
              Boundaries are how you teach people (and yourself) that your time,
              energy, and peace matter.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              10. Do One Thing Slowly Every Day
            </h3>
            <p>
              We rush through everything — meals, conversations, even stillness.
            </p>
            <p>
              Try this: pick one small thing today and do it slowly.
              <br />
              Drink your tea without a screen. Feel the warmth in your hands.
              <br />
              Walk without checking your phone.
            </p>
            <p className="text-[#578d48]">
              When you slow down, life becomes clearer.
            </p>
            <p>
              You stop surviving and start noticing again.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              11. End the Day With Gratitude (Even When It’s Hard)
            </h3>
            <p>
              I used to roll my eyes at the word “gratitude.” It sounded too
              fluffy.
            </p>
            <p>
              But then I tried it — not as a ritual, but as a quiet reflection
              before bed.
            </p>
            <p>
              I’d ask myself: What didn’t go wrong today?
              <br />
              And it changed everything.
            </p>
            <p className="text-[#578d48]">
              Gratitude doesn’t erase pain. It just helps you see that even in the
              dark, there’s still light.
            </p>
          </div>
        </section>

        <section className="space-y-4 mb-10">
          <h2 className="text-secondary text-2xl font-semibold">Conclusion</h2>
          <p>
            You don’t need to “fix” your mind.
            <br />
            You just need to listen to it.
          </p>
          <p>
            These habits aren’t magic tricks — they’re small ways to remind
            yourself that your peace matters.
            <br />
            That you matter.
          </p>
          <p className="text-[#578d48]">
            And if no one has told you this in a while — you’re doing okay.
            <br />
            You’re growing in ways you can’t see yet.
            <br />
            Keep showing up for yourself, even on the quiet days. Especially on
            the quiet days.
          </p>
          <p>
            You don’t need to be perfectly happy.
            <br />
            You just need to keep choosing peace - one small, simple habit at a
            time.
          </p>
        </section>

        <HashtagSection tags={hashtags} />
        <ShareSection />
      </article>
    </>
  );
};

export default Blog11;
